import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiConfig from '../../config/apiConfig';
import { forgetPreviousUser } from '../../services/signInState';

const TOU_VERSION = '2026-04-09-v1';

const DemoLogin = () => {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState('email'); // email | otp
  const [otpCode, setOtpCode] = useState('');
  const [touOpen, setTouOpen] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const touRef = useRef(null);

  const handleTouScroll = useCallback(() => {
    const el = touRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
    if (atBottom) setScrolledToBottom(true);
  }, []);

  const storeAuthAndNavigate = (data) => {
    // Use sessionStorage for demo viewers — cleared on browser close
    const storage = sessionStorage;
    forgetPreviousUser();
    storage.setItem('isAuthenticated', 'true');
    storage.setItem('userRole', 'demo_viewer');
    storage.setItem('authToken', data.token);
    storage.setItem('token', data.token);
    storage.setItem('foundations-token', data.token);
    storage.setItem('userId', '0');
    storage.setItem('userEmail', email);
    storage.setItem('companyName', data.user.company_name);
    storage.setItem('companyId', String(data.user.company_id));
    storage.setItem('isDemoViewer', 'true');

    // Also set localStorage isAuthenticated + userRole for ProtectedRoute compatibility
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('userRole', 'demo_viewer');
    localStorage.setItem('isDemoViewer', 'true');

    if (data.sessionId) {
      storage.setItem('activeSessionId', String(data.sessionId));
      storage.setItem('currentSessionId', String(data.sessionId));
      localStorage.setItem('activeSessionId', String(data.sessionId));
      localStorage.setItem('currentSessionId', String(data.sessionId));
    }
    // Always start demo at assessment results (the main value prop)
    const target = data.sessionId
      ? `/assessment/${data.sessionId}/results`
      : '/coordinator-dashboard';
    navigate(target);
  };

  const handleDemoAccess = async (e) => {
    e.preventDefault();
    setError('');

    if (!acceptedTerms) {
      setError('Please read and accept the Terms of Use to continue.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${apiConfig.getBaseUrl()}/api/auth/demo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, accepted_terms: true, tou_version: TOU_VERSION }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Demo access failed. Please try again.');
        return;
      }

      // OTP flow: server says to verify code
      if (data.step === 'otp_sent') {
        setStep('otp');
        return;
      }

      // Direct JWT (no OTP configured)
      if (data.data) {
        storeAuthAndNavigate(data.data);
      }
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${apiConfig.getBaseUrl()}/api/auth/demo/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp: otpCode }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Verification failed.');
        return;
      }

      storeAuthAndNavigate(data.data);
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── ToU Dialog ──
  const touDialog = touOpen && (
    <div style={overlayStyle}>
      <div style={dialogStyle}>
        <h3 style={{ color: '#e6f1ff', margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
          Demo Terms of Use &amp; NDA
        </h3>
        <div
          ref={touRef}
          onScroll={handleTouScroll}
          style={touScrollStyle}
        >
          <div style={{ color: '#c0c8d8', fontSize: '0.85rem', lineHeight: 1.7 }}>
            <p><strong>CONFIDENTIAL SOFTWARE DEMONSTRATION</strong></p>
            <p>By accessing this demonstration ("Demo") of the Foundations Risk Aperture platform ("Software"), you ("Recipient") agree to the following terms:</p>

            <p><strong>1. Confidentiality.</strong> All information disclosed through this Demo, including but not limited to software functionality, user interfaces, workflows, data models, analytical methodologies, and scoring algorithms, constitutes Confidential Information of Risk Aperture LLC. Recipient shall not disclose, publish, or disseminate any Confidential Information to any third party.</p>

            <p><strong>2. No Recording or Capture.</strong> Recipient shall not capture, record, screenshot, screen-record, photograph, or otherwise reproduce any portion of the Demo by any means, including but not limited to: phone cameras, PrintScreen/screenshot tools, screen recording software (OBS, Loom, etc.), browser extensions, or any other capture mechanism.</p>

            <p><strong>3. AI Extraction Prohibition.</strong> Recipient shall not use any artificial intelligence, machine learning, or automated tools to analyze, replicate, reverse-engineer, or extract information from the Demo. This includes but is not limited to: AI coding assistants (v0, Bolt.new, Cursor, etc.), UI analysis tools (UISnapper, etc.), LLM pipelines, web scrapers, or any automated data extraction mechanism.</p>

            <p><strong>4. No Derivative Works.</strong> Recipient shall not create, develop, or assist in the development of any product, service, or software that is substantially similar to or derived from the concepts, designs, or functionality observed in this Demo.</p>

            <p><strong>5. Session Monitoring.</strong> This Demo session is monitored and logged. All access is timestamped, watermarked with your email address, and may be audited. Security events including developer tools access and screen capture attempts are recorded.</p>

            <p><strong>6. Notification Obligation.</strong> If Recipient becomes aware of any unauthorized disclosure or use of Confidential Information, Recipient shall promptly notify Risk Aperture LLC within 48 hours.</p>

            <p><strong>7. Remedies.</strong> Recipient acknowledges that any breach of these terms may cause irreparable harm and that Risk Aperture LLC shall be entitled to seek injunctive relief in addition to any other remedies available at law or in equity.</p>

            <p><strong>8. Duration.</strong> The obligations under these terms shall survive for a period of five (5) years from the date of access, or for so long as the information remains a trade secret, whichever is longer.</p>

            <p style={{ marginTop: '1.5rem', fontStyle: 'italic', color: '#8892a8' }}>
              Version: {TOU_VERSION}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
          <button
            onClick={() => setTouOpen(false)}
            style={secondaryBtnStyle}
          >
            Cancel
          </button>
          <button
            disabled={!scrolledToBottom}
            onClick={() => { setAcceptedTerms(true); setTouOpen(false); }}
            style={{
              ...primaryBtnSmallStyle,
              opacity: scrolledToBottom ? 1 : 0.4,
              cursor: scrolledToBottom ? 'pointer' : 'not-allowed',
            }}
          >
            {scrolledToBottom ? 'I Accept' : 'Scroll to bottom to accept'}
          </button>
        </div>
      </div>
    </div>
  );

  // ── OTP Step ──
  if (step === 'otp') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <img src="/risk-aperture-logo.svg" alt="Risk Aperture" style={logoStyle} />
            <p style={{ color: '#a8b2cc', margin: '1rem 0 0 0', fontSize: '1rem' }}>
              Check Your Email
            </p>
            <p style={{ color: '#7a8aaa', margin: '0.5rem 0 0 0', fontSize: '0.85rem' }}>
              We sent a 6-digit code to <strong style={{ color: '#c0c8d8' }}>{email}</strong>
            </p>
          </div>

          {error && <div role="alert" style={errorStyle}>{error}</div>}

          <form onSubmit={handleVerifyOTP} style={formStyle}>
            <div>
              <label htmlFor="otp-code" style={labelStyle}>Verification Code</label>
              <input
                id="otp-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
                placeholder="000000"
                style={{ ...inputStyle, textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.25rem' }}
              />
            </div>

            <button
              type="submit"
              disabled={loading || otpCode.length !== 6}
              style={{
                ...primaryButtonStyle,
                opacity: (loading || otpCode.length !== 6) ? 0.5 : 1,
                cursor: (loading || otpCode.length !== 6) ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Verifying...' : 'Verify & Start Demo'}
            </button>
          </form>

          <button
            onClick={() => { setStep('email'); setOtpCode(''); setError(''); }}
            style={{ ...linkBtnStyle, marginTop: '1rem' }}
          >
            Back to email
          </button>
        </div>
      </div>
    );
  }

  // ── Email Step ──
  return (
    <div style={pageStyle}>
      {touDialog}
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ margin: 0, marginBottom: '1rem', lineHeight: 1 }}>
            <img src="/risk-aperture-logo.svg" alt="Risk Aperture" style={logoStyle} />
          </h1>
          <p style={{ color: '#a8b2cc', margin: 0, fontSize: '1rem' }}>
            Self-Guided Demo
          </p>
          <p style={{ color: '#7a8aaa', margin: '0.5rem 0 0 0', fontSize: '0.85rem' }}>
            Explore Foundations Risk Aperture with a read-only preview of a completed assessment
          </p>
        </div>

        {error && <div role="alert" style={errorStyle}>{error}</div>}

        <form onSubmit={handleDemoAccess} style={formStyle}>
          <div>
            <label htmlFor="demo-email" style={labelStyle}>Work Email</label>
            <input
              id="demo-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="you@company.com"
              style={inputStyle}
            />
            <p style={{ color: '#7a8aaa', fontSize: '0.75rem', margin: '0.5rem 0 0 0' }}>
              We'll follow up to see if Foundations is a fit for your team
            </p>
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={() => {
                if (!acceptedTerms) {
                  setTouOpen(true);
                } else {
                  setAcceptedTerms(false);
                }
              }}
              style={{ marginTop: '0.25rem', accentColor: '#00d9ff' }}
            />
            <span style={{ color: '#a8b2cc', fontSize: '0.8rem', lineHeight: 1.5 }}>
              I have read and accept the{' '}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setTouOpen(true); }}
                style={linkBtnStyle}
              >
                Demo Terms of Use &amp; NDA
              </button>
              . This demo contains confidential software. No screenshots, recording, or reverse engineering permitted.
            </span>
          </label>

          <button
            type="submit"
            disabled={loading || !email || !acceptedTerms}
            style={{
              ...primaryButtonStyle,
              opacity: (loading || !email || !acceptedTerms) ? 0.5 : 1,
              cursor: (loading || !email || !acceptedTerms) ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Starting Demo...' : 'Start Demo Tour'}
          </button>
        </form>

        <p style={{ textAlign: 'center', color: '#7a8aaa', fontSize: '0.85rem', marginTop: '1.5rem' }}>
          Already have an account?{' '}
          <a href="/login" style={{ color: '#00d9ff', textDecoration: 'none' }}>Sign in</a>
        </p>
      </div>
    </div>
  );
};

// ── Styles ──

const pageStyle = {
  minHeight: '100vh',
  background: 'radial-gradient(ellipse at bottom, #172a45 0%, #0a192f 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

const cardStyle = {
  width: '100%',
  maxWidth: '460px',
  background: 'rgba(255, 255, 255, 0.06)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  borderRadius: '16px',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
  padding: '2.5rem',
};

const logoStyle = {
  height: 80,
  width: 'auto',
  filter: 'brightness(0) invert(1)',
};

const formStyle = { display: 'flex', flexDirection: 'column', gap: '1.25rem' };

const labelStyle = { display: 'block', fontSize: '0.875rem', color: '#a8b2cc', marginBottom: '0.5rem' };

const inputStyle = {
  width: '100%',
  padding: '0.75rem 1rem',
  background: 'rgba(255, 255, 255, 0.06)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  borderRadius: '8px',
  color: '#e6f1ff',
  fontSize: '0.95rem',
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

const primaryButtonStyle = {
  width: '100%',
  padding: '0.85rem',
  background: 'linear-gradient(135deg, #00d9ff 0%, #0066ff 100%)',
  border: 'none',
  borderRadius: '8px',
  color: '#fff',
  fontSize: '1rem',
  fontWeight: 600,
  fontFamily: 'inherit',
};

const errorStyle = {
  marginBottom: '1.5rem',
  padding: '0.75rem 1rem',
  background: 'rgba(255, 68, 68, 0.08)',
  border: '1px solid rgba(255, 68, 68, 0.25)',
  color: '#ff4444',
  borderRadius: '8px',
  fontSize: '0.875rem',
};

const linkBtnStyle = {
  background: 'none',
  border: 'none',
  color: '#00d9ff',
  cursor: 'pointer',
  padding: 0,
  fontSize: 'inherit',
  fontFamily: 'inherit',
  textDecoration: 'underline',
};

const overlayStyle = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
  padding: '1rem',
};

const dialogStyle = {
  width: '100%',
  maxWidth: '600px',
  background: '#1a2332',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  borderRadius: '12px',
  padding: '1.5rem',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
};

const touScrollStyle = {
  flex: 1,
  overflowY: 'auto',
  maxHeight: '400px',
  padding: '1rem',
  background: 'rgba(0, 0, 0, 0.2)',
  borderRadius: '8px',
  border: '1px solid rgba(255, 255, 255, 0.08)',
};

const secondaryBtnStyle = {
  padding: '0.5rem 1rem',
  background: 'transparent',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '6px',
  color: '#a8b2cc',
  fontSize: '0.85rem',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const primaryBtnSmallStyle = {
  padding: '0.5rem 1.25rem',
  background: 'linear-gradient(135deg, #00d9ff 0%, #0066ff 100%)',
  border: 'none',
  borderRadius: '6px',
  color: '#fff',
  fontSize: '0.85rem',
  fontWeight: 600,
  fontFamily: 'inherit',
};

export default DemoLogin;
